import Raven from 'raven-js';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import * as api from '../../api';
import * as analytics from '../../api/analytics';
import { IDocumentChecklistItem } from '../../declarations';
import { delay } from '../../helpers/sagaHelpers/delay';
import * as actions from './actions';
import { ResaleClosingDashboardActionTypeMap as ActionTypeMap } from './actionTypeMap';
import * as actionTypes from './actionTypes';
import * as selectors from './selectors';

function* fetchListingOffer(action: ActionTypeMap['MAYBE_FETCH_LISTING_OFFER']) {
  const listingOffer = yield select(selectors.listingOffer);
  if (!listingOffer) {
    const { listingOfferId, token } = action.payload;
    if (token) {
      try {
        yield put(actions.beginClaimListingOfferByToken());
        const response = yield call(api.claimListingOfferByToken, token);
        if (response.error) {
          throw Error(response.error);
        }
        yield put(actions.successClaimListingOfferByToken());
        analytics.trackEvent(
          'resale-closing-dashboard',
          'claim-listing-offer-by-token',
          'success',
          { listingOfferId, token },
        );
      } catch (e) {
        yield put(actions.errorClaimListingOfferByToken(e.message));
        analytics.trackEvent('resale-closing-dashboard', 'claim-listing-offer-by-token', 'error', {
          error: e.message,
          listingOfferId,
          token,
        });
        Raven.captureException(e);
        return;
      }
    }
    try {
      yield put(actions.beginLoadingListingOffer());
      const listingOffer = yield call(api.fetchListingOffer, listingOfferId);
      yield put(actions.receiveListingOffer(listingOffer));
      analytics.trackEvent('resale-closing-dashboard', 'fetch-listing-offer', 'success', {
        channel: listingOffer.channel,
        listingOfferId,
        token,
      });
    } catch (e) {
      yield put(actions.errorListingOffer(e.message));
      analytics.trackEvent('resale-closing-dashboard', 'fetch-listing-offer', 'error', {
        error: e.message,
        listingOfferId,
        token,
      });
      Raven.captureException(e);
    }
  }
}

function* fetchListingOffers(action: ActionTypeMap['MAYBE_FETCH_LISTING_OFFERS']) {
  const listingOffers = yield select(selectors.listingOffers);
  if (!listingOffers) {
    yield put(actions.beginLoadingListingOffers());
    try {
      const listingOffers = yield call(api.fetchListingOffers);
      yield put(actions.receiveListingOffers(listingOffers));
      analytics.trackEvent('resale-closing-dashboard', 'fetch-listing-offers', 'success');
    } catch (e) {
      yield put(actions.errorListingOffers(e.message));
      analytics.trackEvent('resale-closing-dashboard', 'fetch-listing-offers', 'error', {
        error: e.message,
      });
      Raven.captureException(e);
    }
  }
}

function* downloadChecklistDocument(action: ActionTypeMap['DOWNLOAD_CHECKLIST_DOCUMENT']) {
  const listingOffer = yield select(selectors.listingOffer);
  if (listingOffer) {
    const { documentId } = action.payload;
    const listingOfferId = listingOffer.id;
    try {
      const response = yield call(api.fetchChecklistDocumentUrl, documentId, listingOfferId);
      if (!response.document_url) {
        throw new Error('Expected document url');
      }
      window.location.assign(response.document_url);
      analytics.trackEvent('resale-closing-dashboard', 'download-checklist-document', 'success', {
        channel: listingOffer.channel,
        documentId,
        listingOfferId,
      });
    } catch (e) {
      analytics.trackEvent('resale-closing-dashboard', 'download-checklist-document', 'failed', {
        channel: listingOffer.channel,
        documentId,
        error: e.message,
        listingOfferId,
      });
      Raven.captureException(e);
    }
  }
}

function* uploadChecklistDocument(action: ActionTypeMap['UPLOAD_CHECKLIST_DOCUMENT']) {
  const listingOffer = yield select(selectors.listingOffer);
  if (listingOffer) {
    const { checklistItemId, documentType, fileBase64DataUrl, name, requestId } = action.payload;
    const listingOfferId = listingOffer.id;
    yield put(actions.beginUploadChecklistDocument(requestId));
    try {
      const checklistItem = yield call(
        api.postChecklistDocument,
        name,
        documentType,
        fileBase64DataUrl,
        listingOfferId,
        checklistItemId,
      );
      yield put(actions.updateChecklistItem(checklistItem));
      yield put(actions.successUploadChecklistDocument(requestId));
      analytics.trackEvent('resale-closing-dashboard', 'upload-checklist-document', 'success', {
        channel: listingOffer.channel,
        checklistItemId,
        documentType,
        listingOfferId,
        name,
      });
    } catch (e) {
      yield put(actions.errorUploadChecklistDocument(requestId, e.message));
      analytics.trackEvent('resale-closing-dashboard', 'upload-checklist-document', 'failed', {
        channel: listingOffer.channel,
        checklistItemId,
        documentType,
        error: e.message,
        listingOfferId,
        name,
      });
      Raven.captureException(e);
    }
  }
}

function* removeChecklistDocument(action: ActionTypeMap['REMOVE_CHECKLIST_DOCUMENT']) {
  const listingOffer = yield select(selectors.listingOffer);
  if (listingOffer) {
    const { documentId } = action.payload;
    const listingOfferId = listingOffer.id;
    yield put(actions.beginRemoveChecklistDocument(documentId));
    try {
      const checklistItem = yield call(api.deleteChecklistDocument, documentId, listingOfferId);
      yield put(actions.updateChecklistItem(checklistItem));
      yield put(actions.successRemoveChecklistDocument(documentId));
      analytics.trackEvent('resale-closing-dashboard', 'remove-checklist-document', 'success', {
        channel: listingOffer.channel,
        documentId,
        listingOfferId,
      });
    } catch (e) {
      yield put(actions.errorRemoveChecklistDocument(documentId, e.message));
      analytics.trackEvent('resale-closing-dashboard', 'remove-checklist-document', 'failed', {
        channel: listingOffer.channel,
        documentId,
        error: e.message,
        listingOfferId,
      });
      Raven.captureException(e);
    }
  }
}

function* submitBuyerRepairPreferences(action: ActionTypeMap['SUBMIT_BUYER_REPAIR_PREFERENCES']) {
  const listingOffer = yield select(selectors.listingOffer);
  if (listingOffer) {
    const { notes, preferredSellerResponse } = action.payload;
    const listingOfferId = listingOffer.id;
    yield put(actions.beginUpdateBuyerRepairPreferences());
    try {
      const buyerRepairPreference = yield call(
        api.postBuyerRepairPreferences,
        listingOfferId,
        notes,
        preferredSellerResponse,
      );
      yield put(actions.updateBuyerRepairPreferences(buyerRepairPreference));
      yield put(actions.successUpdateBuyerRepairPreferences());
      analytics.trackEvent(
        'resale-closing-dashboard',
        'update-buyer-repair-preference',
        'success',
        {
          channel: listingOffer.channel,
          listingOfferId,
          notesPresent: !!notes,
          preferredSellerResponse,
        },
      );
    } catch (e) {
      yield put(actions.errorUpdateBuyerRepairPreference(e.message));
      analytics.trackEvent('resale-closing-dashboard', 'update-buyer-repair-preference', 'failed', {
        channel: listingOffer.channel,
        error: e.message,
        listingOfferId,
        notesPresent: !!notes,
        preferredSellerResponse,
      });
      Raven.captureException(e);
    }
  }
}

function* submitChangeClosingDateRequest(
  action: ActionTypeMap['SUBMIT_CHANGE_CLOSING_DATE_REQUEST'],
) {
  const listingOffer = yield select(selectors.listingOffer);
  if (listingOffer) {
    const { newDate, reason } = action.payload;
    const listingOfferId = listingOffer.id;
    try {
      const customerRequest = yield call(
        api.requestClosingDateChange,
        listingOfferId,
        newDate,
        reason,
      );
      yield put(actions.receiveCustomerRequest(customerRequest));
      analytics.trackEvent('resale-closing-dashboard', 'request-coe-date-change', 'success', {
        channel: listingOffer.channel,
        listingOfferId,
        newDate,
      });
    } catch (e) {
      analytics.trackEvent('resale-closing-dashboard', 'request-coe-date-change', 'failed', {
        channel: listingOffer.channel,
        error: e.message,
        listingOfferId,
        newDate,
      });
      Raven.captureException(e);
    }
  }
}

function* requestGenerateSellerResponse(action: ActionTypeMap['REQUEST_GENERATE_SELLER_RESPONSE']) {
  const listingOffer = yield select(selectors.listingOffer);
  if (listingOffer) {
    const listingOfferId = listingOffer.id;
    try {
      const maxRetries = 5;
      for (let i = 1; i <= maxRetries; i++) {
        const checklistItems: IDocumentChecklistItem[] = yield call(
          api.postGenerateSellerResponse,
          listingOfferId,
        );
        if (checklistItems.every(item => !!item.document)) {
          for (const item of checklistItems) {
            yield put(actions.updateChecklistItem(item));
          }
          yield put(actions.successRequestGenerateSellerResponse());
          analytics.trackEvent(
            'resale-closing-dashboard',
            'request-generate-seller-response',
            'success',
            { channel: listingOffer.channel, listingOfferId },
          );
          break;
        }
        if (i === maxRetries) {
          throw Error('Max retries for response generation');
        }
        yield call(delay, 5000);
      }
    } catch (e) {
      yield put(actions.errorRequestGenerateSellerResponse(e.message));
      analytics.trackEvent(
        'resale-closing-dashboard',
        'request-generate-seller-response',
        'failed',
        { channel: listingOffer.channel, error: e.message, listingOfferId },
      );
      Raven.captureException(e);
    }
  }
}

function* trackEvent(actionParam: ActionTypeMap['TRACK_EVENT']) {
  const { category, action, label, extra } = actionParam.payload;
  analytics.trackEvent(category, action, label, extra);
}

export default function* saga() {
  yield all([
    takeLatest(actionTypes.MAYBE_FETCH_LISTING_OFFER, fetchListingOffer),
    takeLatest(actionTypes.MAYBE_FETCH_LISTING_OFFERS, fetchListingOffers),
    takeLatest(actionTypes.REQUEST_GENERATE_SELLER_RESPONSE, requestGenerateSellerResponse),
    takeLatest(actionTypes.SUBMIT_BUYER_REPAIR_PREFERENCES, submitBuyerRepairPreferences),
    takeLatest(actionTypes.SUBMIT_CHANGE_CLOSING_DATE_REQUEST, submitChangeClosingDateRequest),
    takeEvery(actionTypes.DOWNLOAD_CHECKLIST_DOCUMENT, downloadChecklistDocument),
    takeEvery(actionTypes.REMOVE_CHECKLIST_DOCUMENT, removeChecklistDocument),
    takeEvery(actionTypes.UPLOAD_CHECKLIST_DOCUMENT, uploadChecklistDocument),
    takeEvery(actionTypes.TRACK_EVENT, trackEvent),
  ]);
}
